import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Eye, Save, Send, RotateCcw, Mail, MailOpen, Inbox, Check, Trash2, Sparkles, Pencil } from "lucide-react";
import { toast } from "sonner";

const CLASSES = ["Playgroup", "Nursery", "LKG", "UKG"];
const TEMPLATE_META = [
  { key: "birthday", label: "Birthday Wish", placeholders: "{child_name} {parent_name} {class_name} {turning}" },
  { key: "fee_reminder", label: "Fee Reminder", placeholders: "{child_name} {parent_name} {class_name} {total}" },
  { key: "report_card", label: "Report Card", placeholders: "{child_name} {parent_name} {class_name} {term} {academic_year}" },
];

export default function Settings() {
  const [templates, setTemplates] = useState({});
  const [tab, setTab] = useState("general");
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [replyTo, setReplyTo] = useState("");

  // Broadcast tab state
  const [bClass, setBClass] = useState("__all");
  const [bSubject, setBSubject] = useState("");
  const [bMessage, setBMessage] = useState("");
  const [sending, setSending] = useState(false);

  // Approval queue state
  const [drafts, setDrafts] = useState([]);
  const [editing, setEditing] = useState({}); // {draftId: {subject, message_text}}
  const [busyId, setBusyId] = useState(null);

  const loadDrafts = async () => {
    try {
      const { data } = await api.get("/email-drafts");
      setDrafts(data);
    } catch {}
  };
  useEffect(() => { if (tab === "queue") loadDrafts(); }, [tab]);

  const startEdit = (d) => setEditing({ ...editing, [d.id]: { subject: d.subject, message_text: d.message_text } });
  const cancelEdit = (id) => { const c = { ...editing }; delete c[id]; setEditing(c); };
  const saveEdit = async (id) => {
    setBusyId(id);
    try {
      await api.put(`/email-drafts/${id}`, editing[id]);
      toast.success("Draft updated");
      cancelEdit(id);
      loadDrafts();
    } catch { toast.error("Save failed"); }
    setBusyId(null);
  };
  const approveOne = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/email-drafts/${id}/approve`);
      toast.success("Sent");
      loadDrafts();
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    setBusyId(null);
  };
  const discardOne = async (id) => {
    if (!confirm("Discard this reminder?")) return;
    setBusyId(id);
    try {
      await api.delete(`/email-drafts/${id}`);
      loadDrafts();
    } catch {}
    setBusyId(null);
  };
  const bulkApprove = async () => {
    if (!confirm(`Send all ${drafts.length} pending reminder(s)?`)) return;
    try {
      const { data } = await api.post("/email-drafts/bulk-approve");
      toast.success(`Sent ${data.sent} · Failed ${data.failed}`);
      loadDrafts();
    } catch { toast.error("Bulk send failed"); }
  };
  const generateNow = async () => {
    try {
      const { data } = await api.post("/email-drafts/generate");
      toast.success(`Generated ${data.created} draft(s) · Skipped ${data.skipped}`);
      loadDrafts();
    } catch { toast.error("Generate failed"); }
  };

  const loadAll = async () => {
    const [{ data: tpls }, { data: settings }] = await Promise.all([
      api.get("/email-templates"),
      api.get("/settings"),
    ]);
    const map = {};
    tpls.forEach((t) => {
      map[t.key] = t.saved || t.default;
      map[t.key].__default = t.default;
    });
    setTemplates(map);
    setReplyTo(settings.reply_to || "");
  };
  useEffect(() => { loadAll(); }, []);

  const saveSettings = async () => {
    try {
      await api.put("/settings", { reply_to: replyTo });
      toast.success(replyTo ? `Replies will now go to ${replyTo}` : "Reply-to cleared");
    } catch { toast.error("Could not save"); }
  };

  const save = async (key) => {
    try {
      const t = templates[key];
      await api.put(`/email-templates/${key}`, { subject: t.subject, intro: t.intro, outro: t.outro });
      toast.success("Template saved");
      loadAll();
    } catch { toast.error("Could not save"); }
  };
  const reset = (key) => {
    setTemplates({ ...templates, [key]: { ...templates[key].__default, __default: templates[key].__default } });
  };
  const doPreview = async (key) => {
    try {
      // save first so preview reflects current edits
      const t = templates[key];
      await api.put(`/email-templates/${key}`, { subject: t.subject, intro: t.intro, outro: t.outro });
      const { data } = await api.post(`/email-templates/${key}/preview`);
      setPreview(data);
      setPreviewOpen(true);
    } catch { toast.error("Could not preview"); }
  };

  const sendBroadcast = async () => {
    if (!bSubject.trim() || !bMessage.trim()) { toast.error("Subject and message required"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/emails/class-broadcast", {
        class_name: bClass === "__all" ? null : bClass, subject: bSubject, message: bMessage,
      });
      toast.success(`Sent to ${data.sent} · Failed ${data.failed} · Total recipients ${data.recipients}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Broadcast failed");
    } finally {
      setSending(false);
    }
  };

  const activeTpl = templates[tab] || { subject: "", intro: "", outro: "" };
  const meta = TEMPLATE_META.find((m) => m.key === tab);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-3xl font-black">Settings</h1>
        <p className="text-muted-foreground font-medium">Email templates & class broadcasts.</p>
      </header>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-full h-12 bg-muted p-1 flex-wrap">
          <TabsTrigger value="general" className="rounded-full data-[state=active]:bg-white font-bold">
            <MailOpen size={14} className="mr-1" /> General
          </TabsTrigger>
          {TEMPLATE_META.map((m) => (
            <TabsTrigger key={m.key} value={m.key} className="rounded-full data-[state=active]:bg-white font-bold">
              {m.label}
            </TabsTrigger>
          ))}
          <TabsTrigger value="broadcast" className="rounded-full data-[state=active]:bg-white font-bold">
            <Mail size={14} className="mr-1" /> Class Broadcast
          </TabsTrigger>
          <TabsTrigger value="queue" className="rounded-full data-[state=active]:bg-white font-bold">
            <Inbox size={14} className="mr-1" /> Approval Queue
            {drafts.length > 0 && <span className="ml-2 chip-pink px-2 py-0.5 rounded-full text-[10px] font-bold">{drafts.length}</span>}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="mt-6">
          <Card className="rounded-3xl">
            <CardHeader>
              <CardTitle className="text-xl font-extrabold">Email reply-to inbox</CardTitle>
              <div className="text-sm text-muted-foreground font-medium">
                Where parents' replies to school emails will land. Usually your front-desk mailbox.
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Reply-To email</Label>
                <Input
                  data-testid="reply-to-input"
                  type="email"
                  value={replyTo}
                  onChange={(e) => setReplyTo(e.target.value)}
                  placeholder="frontdesk@kidzkulam.com"
                  className="h-11 rounded-xl"
                />
                <div className="text-xs text-muted-foreground mt-2">
                  Applied to every email the app sends. Leave blank to omit the Reply-To header.
                </div>
              </div>
              <Button data-testid="save-settings" onClick={saveSettings} className="rounded-full">
                <Save size={14} /> Save
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {TEMPLATE_META.map((m) => (
          <TabsContent key={m.key} value={m.key} className="mt-6">
            <Card className="rounded-3xl">
              <CardHeader>
                <CardTitle className="text-xl font-extrabold">{m.label}</CardTitle>
                <div className="text-xs text-muted-foreground font-semibold">
                  Placeholders: <code className="chip-green px-2 py-0.5 rounded font-mono text-[11px]">{m.placeholders}</code>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Subject</Label>
                  <Input
                    data-testid={`tpl-${m.key}-subject`}
                    value={activeTpl.subject || ""}
                    onChange={(e) => setTemplates({ ...templates, [m.key]: { ...activeTpl, subject: e.target.value } })}
                    className="h-11 rounded-xl"
                  />
                </div>
                <div>
                  <Label>Intro (shown before auto-generated content)</Label>
                  <Textarea
                    data-testid={`tpl-${m.key}-intro`}
                    rows={4}
                    value={activeTpl.intro || ""}
                    onChange={(e) => setTemplates({ ...templates, [m.key]: { ...activeTpl, intro: e.target.value } })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label>Outro (closing paragraph)</Label>
                  <Textarea
                    data-testid={`tpl-${m.key}-outro`}
                    rows={4}
                    value={activeTpl.outro || ""}
                    onChange={(e) => setTemplates({ ...templates, [m.key]: { ...activeTpl, outro: e.target.value } })}
                    className="rounded-xl"
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button data-testid={`tpl-${m.key}-preview`} onClick={() => doPreview(m.key)} className="rounded-full">
                    <Eye size={14} /> Preview
                  </Button>
                  <Button data-testid={`tpl-${m.key}-save`} onClick={() => save(m.key)} variant="outline" className="rounded-full">
                    <Save size={14} /> Save
                  </Button>
                  <Button onClick={() => reset(m.key)} variant="ghost" className="rounded-full">
                    <RotateCcw size={14} /> Reset to default
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        ))}

        <TabsContent value="broadcast" className="mt-6">
          <Card className="rounded-3xl">
            <CardHeader>
              <CardTitle className="text-xl font-extrabold">Send a note to every parent in a class</CardTitle>
              <div className="text-xs text-muted-foreground font-semibold">
                Placeholders: <code className="chip-green px-2 py-0.5 rounded font-mono text-[11px]">{"{child_name} {parent_name} {class_name}"}</code>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Audience</Label>
                <Select value={bClass} onValueChange={setBClass}>
                  <SelectTrigger data-testid="broadcast-class" className="h-11 rounded-xl w-64"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all">All classes</SelectItem>
                    {CLASSES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subject</Label>
                <Input data-testid="broadcast-subject" value={bSubject} onChange={(e) => setBSubject(e.target.value)} className="h-11 rounded-xl" placeholder="e.g. School holiday on 15 Aug" />
              </div>
              <div>
                <Label>Message</Label>
                <Textarea data-testid="broadcast-message" rows={7} value={bMessage} onChange={(e) => setBMessage(e.target.value)} className="rounded-xl" placeholder="Dear {parent_name}, ..." />
              </div>
              <Button data-testid="broadcast-send" onClick={sendBroadcast} disabled={sending} className="rounded-full h-12 px-6 font-bold">
                <Send size={16} /> {sending ? "Sending..." : "Send Broadcast"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      

        <TabsContent value="queue" className="mt-6">
          <Card className="rounded-3xl">
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-xl font-extrabold flex items-center gap-2">
                  <Inbox className="text-primary" size={20} /> Fee Reminder Approval Queue
                </CardTitle>
                <div className="text-sm text-muted-foreground font-medium mt-1">
                  Every night at 9:00 IST, drafts are generated for every family with overdue fees. Nothing goes out until you approve.
                </div>
              </div>
              <div className="flex gap-2">
                <Button data-testid="queue-generate" onClick={generateNow} variant="outline" className="rounded-full">
                  <Sparkles size={14} /> Generate now
                </Button>
                {drafts.length > 0 && (
                  <Button data-testid="queue-approve-all" onClick={bulkApprove} className="rounded-full">
                    <Send size={14} /> Approve & send all ({drafts.length})
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {drafts.length === 0 && (
                <div className="py-10 text-center text-muted-foreground">
                  <div className="text-3xl mb-2">🌤️</div>
                  <div className="font-medium">No pending reminders. Tap "Generate now" to draft any overdue accounts.</div>
                </div>
              )}
              {drafts.map((d) => {
                const e = editing[d.id];
                return (
                  <div key={d.id} data-testid={`draft-${d.id}`} className="rounded-3xl border border-border p-4 md:p-5 space-y-3">
                    <div className="flex flex-wrap items-center gap-3">
                      <div className="w-10 h-10 rounded-2xl chip-pink flex items-center justify-center font-black text-sm">
                        {d.child_name.split(" ").map((s) => s[0]).slice(0, 2).join("")}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-extrabold truncate">{d.child_name} <span className="font-normal text-muted-foreground text-xs">· {d.class_name} · {d.parent_name}</span></div>
                        <div className="text-xs text-muted-foreground truncate">
                          To: {d.parent_email} · {d.items} item{d.items > 1 ? "s" : ""} · ₹{Math.round(d.amount).toLocaleString()}
                        </div>
                      </div>
                      {!e && (
                        <div className="flex gap-2 flex-wrap">
                          <button onClick={() => startEdit(d)} className="chip-teal px-3 py-2 rounded-full text-xs font-bold flex items-center gap-1"><Pencil size={12} /> Edit</button>
                          <button onClick={() => discardOne(d.id)} disabled={busyId === d.id} className="chip-amber px-3 py-2 rounded-full text-xs font-bold flex items-center gap-1"><Trash2 size={12} /> Discard</button>
                          <button data-testid={`draft-approve-${d.id}`} onClick={() => approveOne(d.id)} disabled={busyId === d.id} className="chip-green px-3 py-2 rounded-full text-xs font-bold flex items-center gap-1"><Check size={12} /> Approve & send</button>
                        </div>
                      )}
                    </div>

                    {e ? (
                      <div className="space-y-2 rounded-2xl bg-muted/40 p-3">
                        <div>
                          <Label className="text-xs">Subject</Label>
                          <Input
                            data-testid={`draft-subject-${d.id}`}
                            value={e.subject}
                            onChange={(ev) => setEditing({ ...editing, [d.id]: { ...e, subject: ev.target.value } })}
                            className="h-10 rounded-xl"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Message</Label>
                          <Textarea
                            data-testid={`draft-body-${d.id}`}
                            rows={5}
                            value={e.message_text}
                            onChange={(ev) => setEditing({ ...editing, [d.id]: { ...e, message_text: ev.target.value } })}
                            className="rounded-xl"
                          />
                          <div className="text-[10px] text-muted-foreground mt-1">The itemized dues table will be appended automatically below your message.</div>
                        </div>
                        <div className="flex gap-2">
                          <Button data-testid={`draft-save-${d.id}`} size="sm" onClick={() => saveEdit(d.id)} className="rounded-full">Save</Button>
                          <Button size="sm" variant="outline" onClick={() => cancelEdit(d.id)} className="rounded-full">Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-2xl bg-muted/30 p-3 text-sm whitespace-pre-wrap">
                        <div className="font-bold text-xs uppercase tracking-widest text-muted-foreground mb-1">Subject</div>
                        <div className="font-bold mb-2">{d.subject}</div>
                        <div className="font-bold text-xs uppercase tracking-widest text-muted-foreground mb-1">Message</div>
                        {d.message_text}
                      </div>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        </TabsContent>
        </Tabs>

      {previewOpen && preview && (
        <div
          className="fixed inset-0 bg-black/40 z-50 grid place-items-center p-4"
          onClick={() => setPreviewOpen(false)}
        >
          <div
            className="bg-white rounded-3xl overflow-hidden max-w-2xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Preview</div>
                <div className="font-extrabold">{preview.subject}</div>
              </div>
              <button onClick={() => setPreviewOpen(false)} className="px-3 py-1 rounded-full bg-muted font-semibold text-sm">Close</button>
            </div>
            <iframe title="preview" srcDoc={preview.html} className="w-full flex-1 border-0 bg-[#f7f5ee]" />
          </div>
        </div>
      )}
    </div>
  );
}
